import moment from 'moment'
import { useEffect, useState } from 'react'
import { Container, Dropdown } from 'react-bootstrap'
import { FaDollarSign } from 'react-icons/fa6'
import { RxCross1 } from 'react-icons/rx'
import { useLocation, useNavigate } from 'react-router-dom'
import { pause, reportingcard1, reportingcard2 } from '../../../../assests/Icons/icon'
import searchIcon from '../../../../assests/svg/searchIcon.svg'
import getPaid from '../../../../assests/payout.jpg'
import { notifyBugsnagError, removeDashed, showToast } from '../../../../common/utils/Functions'
import DashboardLayout from '../../../../components/layouts/DashboardLayout'
import InactiveActiveModal from '../../../../components/modals/InactiveActiveModal/InactiveActiveModal'
import PageHeading from '../../../../components/pageHeading'
import RangeDateModal from '../../../../components/rangeDateModal/rangeDateModal'
import ReactExcel from '../../../../components/reactExcel/ReactExcel'
import ReactTable from '../../../../components/reactTable/ReactTable'
import ReportingCards from '../../../../components/reportingCards'
import InputField from '../../../../components/theme/InputField'
import ReactSelectField from '../../../../components/theme/ReactSelectField'
import useFetch from '../../../../hooks/useFetch'
import usePaginatedOrgOptions from '../../../../hooks/usePaginatedOrgOptions'
import { cardDataType } from '../../../../types/common'
import { OrganizationItem } from '../../../../types/organization'
import styles from './escrowBalances.module.scss'

const EscrowBalances = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [searchValue, setSearchValue] = useState("");
    const {
        options: filterValues,
        loading: orgLoading,
        loadMore: loadMoreOrgs,
        handleInputChange: handleOrgSearch,
    } = usePaginatedOrgOptions();
    const [sortBy, setSortBy] = useState<1 | 2 | 3 | null>(null);
    const [pageLimit, setPageLimit] = useState<number>(10);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [showRange, setShowRange] = useState(false);
    const [organizationUuid, setOrganizationUuid] = useState(null);
    const [pauseTransfers, setPauseTransfers] = useState(false);
    const [manualBalanceTransfer, setManualBalanceTransfer] = useState(false);

    const [excelData, setExcelData] = useState<any>([]);
    const [escrowData, setEscrowData] = useState<any>([]);
    const [selectedItem, setSelectedItem] = useState<any>();
    const [selectedFilter, setSelectedFilter] = useState<{
        value: string;
        label: string;
    } | null>(null);
    const handleShowRange = () => setShowRange(true);
    const handleCloseRange = () => setShowRange(false);
    const togglePause = () => setPauseTransfers(!pauseTransfers);
    const handleTogglePause = () => setPauseTransfers(false);
    const toggleManualBalanceTransfer = () => setManualBalanceTransfer(!manualBalanceTransfer);
    const handleToggleManualBalanceTransfer = () => setManualBalanceTransfer(false);

    /** Api definition */
    const [
        getEscrowData,
        {
            response: escrowDataResponse,
            error: escrowDataError,
            loading: escrowDataLoading,
        },
    ] = useFetch("/dashboard/all-escrow-tips", {
        method: "POST",
    });

    const [
        pauseTransfersApi,
        { response: pauseResponse, loading: pauseLoading, error: pauseError },
    ] = useFetch(`/organization/pause-resume-transfers/${selectedItem?.organization_details?.user_details?.uuid}`, {
        method: "post",
    });

    const [
        manualBalanceTransferApi,
        { response: manualBalanceTransferResponse, loading: manualBalanceTransferLoading, error: manualBalanceTransferError },
    ] = useFetch(`/admin/manual-balance-transfer/${selectedItem?.organization_details?.user_details?.uuid}`, {
        method: "post",
    });


    const [
        getExcelData,
        {
            response: excelDataResponse,
            error: excelDataError,
            loading: excelDataLoading,
        },
    ] = useFetch("/dashboard/all-escrow-tips", {
        method: "POST",
    });

    useEffect(() => {
        if (pauseResponse) {
            showToast(pauseResponse.message as string, "success");
            handleTogglePause();
            // Refresh the data after successful pause/unpause
            getEscrowData({
                start: (currentPage - 1) * pageLimit,
                limit: pageLimit,
                startDate: startDate,
                endDate: endDate,
                organization_uuid: organizationUuid,
                sortedBy: sortBy,
                search: searchValue,
            });
        }
        if (pauseError) {
            showToast(pauseError.message as string, "error");
        }
    }, [pauseResponse, pauseError]);

    useEffect(() => {
        if (manualBalanceTransferResponse) {
            showToast(manualBalanceTransferResponse.message as string, "success");
            handleToggleManualBalanceTransfer();
            // Refresh the data after successful transfer
            getEscrowData({
                start: (currentPage - 1) * pageLimit,
                limit: pageLimit,
                startDate: startDate,
                endDate: endDate,
                organization_uuid: organizationUuid,
                sortedBy: sortBy,
                search: searchValue,
            });
        }
        if (manualBalanceTransferError) {
            showToast(manualBalanceTransferError.message as string, "error");
        }
    }, [manualBalanceTransferResponse, manualBalanceTransferError]);

    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                getEscrowData({
                    start: (currentPage - 1) * pageLimit,
                    limit: pageLimit,
                    sortedBy: sortBy,
                    search: searchValue,
                    startDate,
                    endDate,
                    organization_uuid: organizationUuid

                });
            } catch (e: any) {
                notifyBugsnagError(e, { api: "getEscrowData" });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [
        searchValue,
        currentPage,
        pageLimit,
        searchValue,
        sortBy,
        startDate,
        endDate,
        organizationUuid,
    ]);


    useEffect(() => {
        if (escrowDataResponse) {
            setTotalRecords(escrowDataResponse.data?.grouped_total_records ?? escrowDataResponse.data?.totalRecords ?? 0);
            let updatedList;
            updatedList = escrowDataResponse.data?.list?.map((item: any) => ({
                ...item,
                organizationName: item?.organization_details?.name ?? removeDashed,
                stripe_status: item?.organization_details?.stripe_status,
                escrowBalance: item?.total_net_tip_amount ? item?.total_net_tip_amount?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                }) : removeDashed,
            }))

            setEscrowData(updatedList);
        } if (escrowDataError) {
            showToast(escrowDataError.message as string, "error");
        }
    }, [escrowDataResponse, escrowDataError])

    const handleExcelApi = () => {
        try {
            getExcelData({
                start: 0,
                limit: -1,
                startDate,
                endDate,
                organization_uuid: organizationUuid,
                sortedBy: sortBy,
                search: searchValue,
            });
        } catch (e: any) {
            notifyBugsnagError(excelDataError, {
                api: "getExcelData",
            });
        }
    };

    const excelHeaders =
        [
            { key: "org_name", header: "Organization Name	", width: 40 },
            { key: "escrow_balance", header: "Escrow Balance", width: 40 },
            { key: "stripe_status", header: "Stripe Account Number", width: 40 },
            { key: "tip_fees", header: "TopTipper Fee", width: 40 },
            {
                key: "net_tip_amount",
                header: "Net Tip Amount",
                width: 40,
            },
            { key: "time_of_tip", header: "Time Of Tip", width: 40 },
            { key: "date", header: "Date", width: 40 },

            { key: "customer_name", header: "Customer Name", width: 40 },
            { key: "note", header: "Note", width: 200 },
        ]

    useEffect(() => {
        if (excelDataResponse) {
            let updateOrgExcel;
            updateOrgExcel = excelDataResponse.data.list?.map((item: any) => {
                return {
                    org_name: item?.organization_details?.name ?? removeDashed,
                    escrow_balance: item?.total_net_tip_amount ? item?.total_net_tip_amount?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                    }) : removeDashed,
                    stripe_status: (item?.stripe_connect_account_id && item?.is_escrowed === 0) ? item?.stripe_connect_account_id : removeDashed,
                    tip_fees: item?.total_top_tipper_fees ? item?.total_top_tipper_fees?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                    }) : removeDashed,
                    net_tip_amount: item?.total_net_tip_amount ? item?.total_net_tip_amount?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                    }) : removeDashed,
                    time_of_tip: item?.createdAt
                        ? moment.utc(item?.createdAt).subtract(4, "hours")?.format("HH:mm A")
                        : removeDashed,
                    date: moment(item?.createdAt).format("MM/DD/YYYY") ?? removeDashed,
                    customer_name: item?.tipper_details?.name ?? removeDashed,
                    note: item?.note ?? removeDashed,
                };
            });

            setExcelData([...updateOrgExcel]);
        }
        if (excelDataError) {
            showToast(excelDataError.message as string, "error");
        }
    }, [excelDataResponse, excelDataError]);

    const handleDateFilter = (value: string) => {
        const today = moment().format("MM/DD/YYYY");
        let startDate = null;
        let endDate = null;
        switch (value) {
            case "week":
                startDate = moment().startOf("week").format("MM/DD/YYYY");
                break;
            case "month":
                startDate = moment().startOf("month").format("MM/DD/YYYY");
                break;
            case "today":
                startDate = today;
                break;
            case "all":
                break;
            default:
                break;
        }
        if (value !== "all" && startDate) {
            endDate = today;
        }
        setStartDate(startDate);
        setEndDate(endDate);
    };

    useEffect(() => {
        if (location.state?.hasOwnProperty("filterValue")) {
            setSelectedFilter(location.state.filterValue);
            handleDateFilter(location.state.filterValue.value);
        }
    }, [location]);


    const filters = [
        { value: "all", label: "All" },
        { value: "today", label: "Today" },
        { value: "week", label: "This Week" },
        { value: "month", label: "This Month" },
        { value: "Custom", label: "Custom" },
    ];

    const options = [
        { value: 1, label: "Date" },
        { value: 2, label: "Highest Tip Amount" },
        { value: 3, label: "Lowest Tip Amount" },
    ];

    const CardsData: cardDataType[] =
        [
            {
                icon: reportingcard1,
                title: "Organizations with Escrow Balances",
                value: escrowDataResponse?.data?.grouped_total_records ? escrowDataResponse?.data?.grouped_total_records : removeDashed,
                amount: "",
            },
            {
                icon: reportingcard2,
                title: "Total Amount in Escrow",
                value: "",
                amount: escrowDataResponse?.data?.total_escrow_amount ? escrowDataResponse?.data?.total_escrow_amount?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                }) : removeDashed
            },
        ]

    const headings =
        [
            {
                label: "Organization Name",
                key: "organizationName",
            },
            { label: "Stripe Status", key: "stripe_status", activeColumn: true },
            { label: "Escrow Balance", key: "escrowBalance" },
        ]

    const actionButtonOptions =

        [
            {
                name: "Pause Transfers",
                icon: pause,
                onClick: (item: any) => {
                    setSelectedItem(item);
                    togglePause();
                },

            },
            {
                name: "Manual Balance Transfer",
                icon: <FaDollarSign />,
                onClick: (item: any) => {
                    setSelectedItem(item);
                    toggleManualBalanceTransfer();
                },
            },


        ]


    const DropdownItems = () => {
        return (
            <div
                style={{
                    border: "1px solid #A9B0BF",
                    paddingLeft: "11px",
                    width: "34px",
                    borderRadius: "4px",
                    backgroundColor: "#ededed",
                }}
            >
                {" "}
                <Dropdown
                    align="end"
                    className={styles.dropDots}
                >
                    <Dropdown.Toggle variant="light" id="dropdown-basic" style={{ backgroundColor: 'transparent', border: 'none', transform: 'rotate(90deg)', color: '#676d7a', fontSize: '20px' }}>
                        ...
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                        style={{
                            maxWidth: "max-content",
                            minWidth: "150px",
                            zIndex: "99",
                        }}
                    >
                        <Dropdown.Item
                            onClick={() => {
                                navigate('/reporting')
                            }}
                        >
                            {" "}
                            Tip Transactions
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => {
                                navigate('/reporting/conversion-tracking')
                            }}
                        >
                            {" "}
                            Conversion Tracking
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => {
                                navigate('/reporting/payout-transactions')
                            }}
                        >
                            {" "}
                            Payout Transactions
                        </Dropdown.Item>

                    </Dropdown.Menu>
                </Dropdown>
            </div>
        );
    };


    return (
        <DashboardLayout>
            <div className={styles.dash_page}>
                <Container>
                    <PageHeading
                        heading={

                            <span style={{ display: 'flex', gap: '10px' }}>
                                Escrow Balances
                                <DropdownItems />
                            </span>
                        }
                        subHeading="Here is the information about all your reports."
                    >
                        <div className={styles.search}>
                            <div className={styles.searchField}>
                                <InputField
                                    name="search_organization_name"
                                    icon={searchIcon}
                                    placeholder="Search by organization Name"
                                    value={searchValue}
                                    onChange={(e) => {
                                        setCurrentPage(1);
                                        setSearchValue(e.target.value);
                                    }}
                                    autoComplete="off"
                                />
                                {searchValue ? (
                                    <span
                                        className={styles.crossIcon}
                                        onClick={() => setSearchValue("")}
                                    >
                                        <RxCross1 />
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </PageHeading>
                    <div className={styles.tipsWrap}>
                        <ReactSelectField
                            name="sortBy"
                            options={options}
                            label="Sort By"
                            onChange={(e: any) => {
                                setCurrentPage(1);
                                setSortBy(e.value);
                            }}
                        />
                        <ReactSelectField
                            name="Sort By"
                            options={filters}
                            label="Filter By"
                            value={selectedFilter}
                            onChange={(e: any) => {
                                if (e.value === "Custom") {
                                    handleShowRange();
                                }
                                handleDateFilter(e.value);
                                setSelectedFilter(e);
                            }}
                        />
                        <ReactSelectField
                            name={"organizations"}
                            options={filterValues}
                            label={"Organizations"}
                            onLoadMore={loadMoreOrgs}
                            isLoadingMore={orgLoading}
                            onInputChange={handleOrgSearch}
                            onChange={(e: any) => {
                                setOrganizationUuid(e.value);
                            }}
                        />
                        <ReactExcel
                            setExcelData={setExcelData}
                            loading={excelDataLoading}
                            onClick={handleExcelApi}
                            buttonTitle="Export CSV"
                            fileType="xlsx"
                            fileName="Escrow Tips"
                            coulmns={excelHeaders}
                            excelData={excelData}
                        />
                        <ReportingCards cardData={CardsData} />

                    </div>
                    <ReactTable
                        data={escrowData || [] || []}
                        headings={headings}
                        actionColumn={true}
                        actionButtonOptions={actionButtonOptions}
                        currentPage={currentPage}
                        itemPerPage={pageLimit}
                        totalItems={totalRecords}
                        setPerPageLimit={setPageLimit}
                        pageLimit={pageLimit}
                        setCurrentPage={setCurrentPage}
                        pagination={true}
                        statusColumn={false}
                        loading={escrowDataLoading || !escrowData}
                    />
                    {selectedFilter?.value === "Custom" && (
                        <RangeDateModal
                            show={showRange}
                            handleClose={handleCloseRange}
                            setEndDate={setEndDate}
                            setStartDate={setStartDate}
                        />
                    )}

                    <InactiveActiveModal
                        loading={pauseLoading}
                        handleToggle={handleTogglePause}
                        title={`Are you sure you want to ${selectedItem?.organization_details?.transfer_status ? "pause" : "Unpause"
                            } transfers?`}
                        show={pauseTransfers}
                        heading={
                            selectedItem?.organization_details?.transfer_status ? "Pause Transfers" : "Unpause Transfers"
                        }
                        confirm={() => {
                            pauseTransfersApi({
                                transfer_status:
                                    selectedItem?.organization_details?.transfer_status ? 0 : 1,
                            });
                        }}
                    />

                    <InactiveActiveModal
                        loading={manualBalanceTransferLoading}
                        handleToggle={handleToggleManualBalanceTransfer}
                        title={`Confirm Manual Balance Transfer of $${selectedItem?.total_net_tip_amount?.toFixed(2) || '0.00'} to ${selectedItem?.organization_details?.name || 'Organization'}`}
                        show={manualBalanceTransfer}
                        heading={
                            "Manual Balance Transfer"
                        }
                        confirmButtonText="Confirm"
                        customImage={getPaid}
                        confirm={() => {
                            manualBalanceTransferApi();
                        }}
                    />
                </Container>
            </div>
        </DashboardLayout>
    )
}

export default EscrowBalances